// One hook for the "apply a batch, get one Undo" idiom shared by the Inbox
// apply-to-similar prompt/dialog (`#s06`) and the Ask commit step
// (`#foundation` step 5 / `#s07`) -- the execution plan's binding rule that
// "every apply is one batch with one Undo... Undo is a batch operation over
// the whole committed change, not per row" (WS-5 owned-hooks list names a
// single `useBatchUndo`, not one hook per feature).
//
// Neither the categorize-batch write nor its undo has an M1 endpoint (§9
// conflict #9 covers Ask; no write endpoint for bulk categorize appears
// anywhere in the M1 interface table either), so both mutations reject with
// `BatchUnavailableError` rather than issuing a network call. Counts shown
// in the calling UI ("3 items · −$92.13") must come from data already on
// hand (the fixture, or a real API once one exists) -- this hook never
// invents one.
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

export class BatchUnavailableError extends Error {
  constructor() {
    super('Applying or undoing a batch has no M1 endpoint. No demonstration result has been substituted.');
    this.name = 'BatchUnavailableError';
  }
}

export const applyBatchInputSchema = z.object({
  scope: z.enum(['similar_this_month', 'always_condition', 'single', 'ask_commit']),
  category_path: z.array(z.string()).optional(),
  business_use: z.enum(['none', 'mixed', 'primary']).optional(),
  transaction_ids: z.array(z.string()),
  condition_label: z.string().optional(),
});
export type ApplyBatchInput = z.infer<typeof applyBatchInputSchema>;

export const applyBatchResultSchema = z.object({
  batch_id: z.string(),
  applied_count: z.number(),
  applied_amount: z.string().nullable(), // signed decimal string sum, or null when not supplied
  message: z.string(),
});
export type ApplyBatchResult = z.infer<typeof applyBatchResultSchema>;

export function useBatchUndo() {
  const apply = useMutation<ApplyBatchResult, BatchUnavailableError, ApplyBatchInput>({
    mutationFn: async () => {
      throw new BatchUnavailableError();
    },
    retry: false,
  });

  const undo = useMutation<void, BatchUnavailableError, string>({
    mutationFn: async () => {
      throw new BatchUnavailableError();
    },
    retry: false,
  });

  return { apply, undo };
}
